import { useEffect, useState } from "react";
import { HeadlineTypeEnum } from "../../assets/utils/enums/headline";
import { ThemeTypeEnum } from "../../assets/utils/validation/types";
import { BodyDiv } from "../../components/components-style";
import AppLayout, { MainBody } from "../../components/layout/app/layout";
import Headline from "../../components/ui/headline";
import {
  CaptionText,
  LittleSubHeadline,
  TextBody,
} from "../../components/typography";
import Input from "../../components/ui/inputs";
import DeadlinePicker from "../../components/ui/deadline-picker";
import ErrorBlock from "../../components/ui/error-text";
import { useFormValidation } from "../../assets/utils/hooks/useFormsValidation";
import { validateTask } from "../../assets/utils/validation/form/validator-task";
import {
  AddIconBtn,
  AddMemberBody,
  AddMemberItem,
  GeneralItemBlock,
} from "../project/add";
import {
  EndpointsEnum,
  NameIdType,
  prioritiesObj,
  PriorityEnum,
  ProjectMember,
} from "../../assets/utils/enums/project";
import {
  API_URL_PRIORITY,
  API_URL_PROJECT,
  API_URL_SELLING,
  API_URL_TASK,
} from "../../constants";
import getData from "../../api/project-request";
import { Button } from "../../components/ui/button";
import { COLORS } from "../../assets/utils/enums/colors";
import { AnimatePresence, motion } from "framer-motion";
import plusIcon from "../../assets/images/icons/circle-add-project.svg";
import minusIcon from "../../assets/images/icons/circle-minus-project.svg";
import toast from "react-hot-toast";
import CustomToast from "../../components/ui/custom-toast";
import { ToastTypeEnum } from "../../assets/utils/enums/toast";
import { ValidationFormsModeEnum } from "../../assets/utils/validation/form/types";
import { authFetch } from "../../api/authFetch";
import { useNavigate } from "react-router-dom";
import { formatIOS } from "../../assets/utils/date-formatter";
import { DropdownTypeEnum } from "../../assets/utils/enums/table";

export default function AddTaskPage() {
  const [name, setName] = useState<string>("");
  const [deadline, setDeadline] = useState<string>("");
  const [project, setProject] = useState<NameIdType>({ id: "", name: "" });
  const [selling, setSelling] = useState<NameIdType>({ id: "", name: "" });
  const [description, setDescription] = useState<string>("");
  const [priority, setPriority] = useState<string>(PriorityEnum.LOW);
  const [members, setMembers] = useState<ProjectMember[]>([
    { email: "", roleName: "" },
  ]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [priorities, setPriorities] = useState<NameIdType[]>([]);
  const [responsibleForMembers, setResponsibleForMembers] = useState<
    { fullName: string; email: string }[]
  >([]);
  const [filterProjects, setFilterProjects] = useState<NameIdType[]>([]);
  const [filterSellings, setFilterSellings] = useState<NameIdType[]>([]);
  const [projectQuery, setProjectQuery] = useState<string>("");
  const [sellingQuery, setSellingQuery] = useState<string>("");

  const { errors, validate } = useFormValidation(validateTask);
  const navigate = useNavigate();

  useEffect(() => {
    const getMembersUsers = async () => {
      const data = await getData(
        `${API_URL_PROJECT}/${EndpointsEnum.RESPONSIBLE_USER}?query=`
      );
      setResponsibleForMembers(data.slice(0, 4));
    };

    const getPriorities = () => {
      setIsLoading(true);
      setTimeout(async () => {
        const data = await getData(`${API_URL_PRIORITY}/get-all`);
        setPriorities(data);
        setIsLoading(false);
      }, 50);
    };

    getPriorities();
    getMembersUsers();
  }, []);

  useEffect(() => {
    if (projectQuery !== project.name) {
      setProject({ id: "", name: "" });
    }

    const getProjects = async () => {
      try {
        const data = await getData(
          `${API_URL_PROJECT}/get-projects?query=${projectQuery}&sellingId=${selling.id}`
        );
        setFilterProjects(Array.isArray(data) ? data.slice(0, 3) : []);
        
        if (data.length === 0) return;

        if (projectQuery.toLowerCase() !== data[0].name.toLowerCase()) {
          return;
        }
        if (selling.id !== "" && data.length !== 0) {
          setProject({ id: data[0].id, name: data[0].name });
          setProjectQuery(data[0].name);
        }

      } catch {
        setFilterProjects([]);
      }
    };

    getProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectQuery, selling.id]);

  useEffect(() => {
    setIsLoading(true);
    if (sellingQuery !== selling.name) {
      setSelling({ id: "", name: "" });
    }
    const getSellings = async () => {
      try {
        const data = await getData(
          `${API_URL_SELLING}/search-by?query=${sellingQuery}&projectId=${project.id}`
        );
        setFilterSellings(Array.isArray(data) ? data.slice(0, 3) : []);
      } catch {
        setFilterSellings([]);
      } finally {
        setIsLoading(false);
      }
    };

    getSellings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellingQuery, project.id]);

  const handleAdd = () => {
    setMembers([
      ...members,
      {
        email: "",
        roleName: "",
        fullName: "",
        filteredResponsible: responsibleForMembers,
      },
    ]);
  };

  const handleChange = async (index: number, value: string) => {
    const updated = [...members];
    updated[index]["email"] = value;

    const existingList = updated[index]["filteredResponsible"];
    if (Array.isArray(existingList)) {
      updated[index]["fullName"] =
        existingList.find((x) => x.email === value)?.fullName || "";
    } else {
      updated[index]["fullName"] =
        responsibleForMembers.find((x) => x.email === value)?.fullName || "";
    }

    const data = await getData(
      `${API_URL_PROJECT}/${EndpointsEnum.RESPONSIBLE_USER}?query=${value}`
    );
    updated[index].filteredResponsible = data.slice(0, 4);

    setMembers(updated);
  };

  const handleRemove = (index: number) => {
    setMembers(members.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      const { hasErrors } = validate(
        {
          name,
          description,
          deadline,
          project,
          priority,
          selling,
          members,
        },
        ValidationFormsModeEnum.ADD
      );

      if (hasErrors) return;

      const response = await authFetch(`${API_URL_TASK}/create-task`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name,
          description: description,
          deadline: formatIOS(deadline),
          projectId: project.name === "" ? null : project.id,
          sellingId: selling.name === "" ? null : selling.id,
          priorityName: priority,
          users: members
            .filter((item) => item.email.trim() !== "")
            .map((item) => ({ email: item.email })),
        }),
        credentials: "include",
      });

      if (!response.ok) {
        toast((t) => (
          <CustomToast
            t={t}
            type={ToastTypeEnum.WARNING}
            text={"Перевірте коректність введення даних!"}
          />
        ));
        return;
      }
      toast((t) => (
        <CustomToast
          t={t}
          type={ToastTypeEnum.SUCCESS}
          text={"Дані введені коректно, завдання додано!"}
        />
      ));
      setTimeout(() => {
        navigate("/tasks/main");
      }, 300);
    } catch (error) {
      console.error("Fetch error:", error);
      toast((t) => (
        <CustomToast
          t={t}
          text={"Упс, щось пішло не так\nПроблема з сервером."}
          type={ToastTypeEnum.ERROR}
        />
      ));
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 150);
    }
  };

  return (
    <AppLayout
      className="d-flex flex-column align-items-center"
      blockType={"item-form"}
      loaderState={isLoading}
    >
      <Headline
        headline={HeadlineTypeEnum.TASKS}
        theme={ThemeTypeEnum.LIGHT}
        btn_text="Повернутися до Завдань"
        btn_link="/tasks/main"
      />
      <MainBody className="align-items-stretch">
        <BodyDiv
          data-class="add-block"
          className="d-flex flex-column align-items-center"
        >
          <CaptionText>Основні дані про завдань</CaptionText>
          <div className="w-100">
            <Input
              attribute="nameOfTask"
              value={name}
              setValue={setName}
              errorText={errors.name}
              label="Назва"
              placeholder="Введіть завдання"
            />
            <span className="w-100 d-flex gap-sm-3 flex-sm-row flex-column">
              <Input
                attribute={DropdownTypeEnum.PROJECTS}
                item={project}
                setItem={setProject}
                value={projectQuery}
                setValue={setProjectQuery}
                errorText={errors.project}
                itemsNameIdArr={filterProjects}
                label="Проєкт"
                placeholder="Введіть назву"
              />

              <Input
                attribute={DropdownTypeEnum.SELLINGS}
                item={selling}
                setItem={setSelling}
                value={sellingQuery}
                setValue={setSellingQuery}
                errorText={errors.selling}
                itemsNameIdArr={filterSellings}
                label="Продажі"
                placeholder="Введіть назву"
              />
            </span>
            <DeadlinePicker value={deadline} setValue={setDeadline} />
            {errors.deadline && <ErrorBlock errorText={errors.deadline} />}
          </div>
        </BodyDiv>
        <BodyDiv
          data-class="add-block-other"
        >
          <span className="w-100 d-flex flex-column align-items-center">
            <CaptionText>Опис</CaptionText>
            <Input
              attribute="description"
              value={description}
              setValue={setDescription}
              errorText={""}
              label=""
              placeholder="Введіть опис"
            />
          </span>
          <span className="w-100 d-flex flex-column align-items-center">
            <CaptionText>Пріоритет</CaptionText>
            <GeneralItemBlock>
              {priorities.map((item: NameIdType) => {
                const isActive: boolean = priority === item.name;
                return (
                  <Button
                    key={item.id}
                    bgcolor={
                      isActive
                        ? prioritiesObj[item.name as PriorityEnum].bgColor
                        : "transparent"
                    }
                    txtcolor={
                      isActive
                        ? prioritiesObj[item.name as PriorityEnum].txColor
                        : COLORS["bg-text-color"]
                    }
                    brcolor={
                      isActive
                        ? prioritiesObj[item.name as PriorityEnum].bgColor
                        : COLORS["bg-text-color"]
                    }
                    width="100%"
                    type="large"
                    brradius="24px"
                    className=""
                    onClick={() => {
                      setPriority(item.name);
                    }}
                  >
                    <TextBody>{item.name as PriorityEnum}</TextBody>
                    {isActive && (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 32 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M28.708 8.70799L14.708 22.708C14.6151 22.8011 14.5048 22.875 14.3833 22.9254C14.2618 22.9758 14.1315 23.0018 14 23.0018C13.8685 23.0018 13.7382 22.9758 13.6167 22.9254C13.4952 22.875 13.3849 22.8011 13.292 22.708L5.29199 14.708L6.70799 13.292L14 20.586L27.294 7.29199L28.708 8.70799Z"
                          fill={
                            isActive
                              ? prioritiesObj[item.name as PriorityEnum].txColor
                              : "transparent"
                          }
                        />
                      </svg>
                    )}
                  </Button>
                );
              })}
            </GeneralItemBlock>
          </span>
        </BodyDiv>
      </MainBody>
      <BodyDiv
        data-class="add-block-members"
        className="d-flex flex-column align-items-start"
      >
        <span className="d-flex align-items-center justify-content-between w-100">
          <CaptionText>Додати виконавців цього завдання</CaptionText>
          <AddIconBtn src={plusIcon} alt="add-icon" onClick={handleAdd} />
        </span>
        <AnimatePresence>
          {members.map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="w-100"
            >
              <AddMemberItem key={index}>
                <AddMemberBody>
                  <Input
                    attribute="members"
                    value={member.email}
                    setValue={(val) => handleChange(index, val as string)}
                    errorText=""
                    label=""
                    contactsArr={
                      member.filteredResponsible || responsibleForMembers
                    }
                    placeholder="Введіть пошту / ім’я"
                  />
                  {member.fullName && (
                    <motion.div
                      key={member.fullName}
                      initial={{ opacity: 0, y: 0, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 0, scale: 1 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      style={{ overflow: "visible", paddingTop: "6px" }}
                    >
                      <Button
                        bgcolor={COLORS["light-blue"]}
                        txtcolor={COLORS.blue}
                        brcolor={COLORS.blue}
                        width="auto"
                        className="px-4"
                        brradius="24px"
                        type="large"
                      >
                        <TextBody>{member.fullName}</TextBody>
                      </Button>
                    </motion.div>
                  )}
                </AddMemberBody>
                <AddIconBtn
                  src={minusIcon}
                  alt="minus-icon"
                  onClick={() => handleRemove(index)}
                />
              </AddMemberItem>
            </motion.div>
          ))}
        </AnimatePresence>
        {errors.members && <ErrorBlock errorText={errors.members} />}
      </BodyDiv>
      <Button
        bgcolor={COLORS.secondary1}
        txtcolor={COLORS.white}
        brcolor={"transparent"}
        width="30%"
        className="center-btn"
        brradius="24px"
        type="large"
        onClick={handleSave}
      >
        <LittleSubHeadline>Зберегти зміни</LittleSubHeadline>
      </Button>
    </AppLayout>
  );
}
